import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import * as XLSX from 'xlsx';
import FileUploader from './Fileuploader';
import './ChatBot.css';

// URL del backend (ECS Fargate). Migrado desde Railway el 2026-05-19.
const BACKEND_URL = 'https://al-bcd27e7e0130466f87e7390ee3784d32.ecs.us-east-1.on.aws';
const FILES_API_URL = BACKEND_URL;

// Mensajes de bienvenida según idioma
const WELCOME_MESSAGES = {
  es: {
    basic: '👋 ¡Hola! Soy tu **AI Pricing Analytics Engine**. Puedo ayudarte a consultar información del datalake, analizar precios de competencia y generar insights.\n\n✨ **Funciones disponibles:**\n- 📊 Análisis de datos del datalake\n- 🖼️ Análisis de imágenes (gráficos, tablas, etc.)\n- 📈 Procesamiento de archivos Excel\n- 📄 Análisis de documentos PDF\n\n¿Qué te gustaría saber?',
    withFileAccess: '👋 ¡Hola! Soy tu **AI Pricing Analytics Engine**. Puedo ayudarte a consultar información del datalake, analizar precios de competencia y generar insights.\n\n✨ **Funciones disponibles:**\n- 📊 Análisis de datos del datalake\n- 🖼️ Análisis de imágenes (gráficos, tablas, etc.)\n- 📈 Procesamiento de archivos Excel\n- 📄 Análisis de documentos PDF\n- 📁 Referencia a archivos con @nombrearchivo\n\n¿Qué te gustaría saber?',
    cleared: '🔄 Chat limpiado. Soy tu **AI Pricing Analytics Engine**. ¿En qué puedo ayudarte?'
  },
  en: {
    basic: '👋 Hello! I\'m your **AI Pricing Analytics Engine**. I can help you query information from the datalake, analyze competitive pricing, and generate insights.\n\n✨ **Available features:**\n- 📊 Datalake data analysis\n- 🖼️ Image analysis (charts, tables, etc.)\n- 📈 Excel file processing\n- 📄 PDF document analysis\n\nWhat would you like to know?',
    withFileAccess: '👋 Hello! I\'m your **AI Pricing Analytics Engine**. I can help you query information from the datalake, analyze competitive pricing, and generate insights.\n\n✨ **Available features:**\n- 📊 Datalake data analysis\n- 🖼️ Image analysis (charts, tables, etc.)\n- 📈 Excel file processing\n- 📄 PDF document analysis\n- 📁 Reference files with @filename\n\nWhat would you like to know?',
    cleared: '🔄 Chat cleared. I\'m your **AI Pricing Analytics Engine**. How can I help you?'
  }
};

// Placeholders según idioma
const PLACEHOLDERS = {
  es: {
    withFile: 'Escribe tu pregunta sobre el archivo...',
    withFileAccess: 'Escribe tu pregunta o usa @ para referenciar archivos...',
    default: 'Escribe tu pregunta...'
  },
  en: {
    withFile: 'Type your question about the file...',
    withFileAccess: 'Type your question or use @ to reference files...',
    default: 'Type your question...'
  }
};

function ChatBot({ username }) {
  useEffect(() => {
    if (!username) {
      console.error('[CHATBOT] ❌ Username no proporcionado - ChatBot no funcionará correctamente');
    } else {
      console.log('[CHATBOT] ✅ Usuario:', username);
    }
  }, [username]);

  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [initialMessageUpdated, setInitialMessageUpdated] = useState(false);
  const [language, setLanguage] = useState('es'); // Idioma del usuario (es o en)
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      text: WELCOME_MESSAGES.es.basic, // Mensaje inicial por defecto en español
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [thinkingMessage, setThinkingMessage] = useState('');
  const [showThinking, setShowThinking] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileClearTrigger, setFileClearTrigger] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Estado para archivos disponibles y autocompletado
  const [availableFiles, setAvailableFiles] = useState([]);
  const [hasFileAccess, setHasFileAccess] = useState(false); // Indica si el usuario tiene s3_prefix configurado
  const [showFileDropdown, setShowFileDropdown] = useState(false);
  const [fileSearchText, setFileSearchText] = useState('');
  const [selectedFileIndex, setSelectedFileIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [referencedFiles, setReferencedFiles] = useState([]);
  const dropdownRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingMessage, thinkingMessage]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Cargar archivos disponibles cuando se abre el chat
  useEffect(() => {
    if (isOpen && username) {
      loadAvailableFiles();
    }
  }, [isOpen, username]);

  const loadAvailableFiles = async () => {
    try {
      const response = await fetch(`${FILES_API_URL}/api/files/available?username=${encodeURIComponent(username)}`);
      const data = await response.json();
      if (data.success) {
        setAvailableFiles(data.files || []);
        setHasFileAccess(data.hasFileAccess || false);

        // Obtener el idioma del usuario desde el backend
        const userLanguage = data.language || 'es';
        setLanguage(userLanguage);
        console.log('[CHATBOT] 📁 Archivos disponibles:', data.files?.length || 0, '| Acceso a archivos:', data.hasFileAccess, '| Idioma:', userLanguage);

        // Actualizar mensaje inicial según idioma y acceso a archivos
        if (!initialMessageUpdated) {
          const langMessages = WELCOME_MESSAGES[userLanguage] || WELCOME_MESSAGES.es;
          const welcomeText = data.hasFileAccess ? langMessages.withFileAccess : langMessages.basic;

          setMessages(prev => {
            if (prev.length === 1 && prev[0].id === 1) {
              return [{
                ...prev[0],
                text: welcomeText
              }];
            }
            return prev;
          });
          setInitialMessageUpdated(true);
        }
      } else {
        setHasFileAccess(false);
        setAvailableFiles([]);
      }
    } catch (error) {
      console.error('[CHATBOT] Error cargando archivos disponibles:', error);
      setHasFileAccess(false);
      setAvailableFiles([]);
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleFileUpload = (fileData) => {
    setUploadedFile(fileData);
    if (fileData) {
      console.log('[ChatBot] 📁 Archivo cargado:', fileData.filename, '| Tipo:', fileData.type);
    } else {
      console.log('[ChatBot] 🗑️ Archivo eliminado');
    }
  };

  // Funciones para autocompletado de archivos con @
  const handleInputChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setInputValue(value);
    setCursorPosition(cursorPos);

    // Solo procesar referencias @ si el usuario tiene acceso a archivos
    if (!hasFileAccess) {
      setShowFileDropdown(false);
      setFileSearchText('');
      setReferencedFiles([]);
      return;
    }

    // Detectar si estamos escribiendo después de un @
    const textBeforeCursor = value.substring(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@(\S*)$/);

    if (atMatch && availableFiles.length > 0) {
      const searchTerm = atMatch[1].toLowerCase();
      setFileSearchText(searchTerm);
      setShowFileDropdown(true);
      setSelectedFileIndex(0);
    } else {
      setShowFileDropdown(false);
      setFileSearchText('');
    }

    // Detectar archivos referenciados en el texto
    // Buscar archivos que coincidan con los nombres en availableFiles
    const newReferencedFiles = [];
    for (const file of availableFiles) {
      // Escapar caracteres especiales del nombre para usar en regex
      const escapedName = file.original_name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const fileRegex = new RegExp(`@${escapedName}(?:\\s|$)`, 'gi');
      if (fileRegex.test(value)) {
        newReferencedFiles.push(file);
      }
    }
    setReferencedFiles(newReferencedFiles);
  };

  const getFilteredFiles = () => {
    if (!fileSearchText) return availableFiles;
    return availableFiles.filter(file =>
      file.original_name.toLowerCase().includes(fileSearchText.toLowerCase())
    );
  };

  const selectFile = (file) => {
    const textBeforeCursor = inputValue.substring(0, cursorPosition);
    const textAfterCursor = inputValue.substring(cursorPosition);

    // Encontrar donde empieza el @ actual
    const atMatch = textBeforeCursor.match(/@(\S*)$/);
    if (atMatch) {
      const beforeAt = textBeforeCursor.substring(0, textBeforeCursor.length - atMatch[0].length);
      const newValue = beforeAt + '@' + file.original_name + ' ' + textAfterCursor;
      setInputValue(newValue);

      // Actualizar archivos referenciados inmediatamente
      // Buscar archivos que coincidan con los nombres en availableFiles (soporta espacios)
      const newReferencedFiles = [];
      for (const f of availableFiles) {
        const escapedName = f.original_name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const fileRegex = new RegExp(`@${escapedName}(?:\\s|$)`, 'gi');
        if (fileRegex.test(newValue)) {
          newReferencedFiles.push(f);
        }
      }
      setReferencedFiles(newReferencedFiles);
      console.log('[CHATBOT] 📁 Archivo seleccionado:', file.original_name, '| Tipo:', file.file_type, '| Referencias:', newReferencedFiles.length);

      // Mover cursor después del nombre del archivo
      setTimeout(() => {
        if (inputRef.current) {
          const newPos = beforeAt.length + 1 + file.original_name.length + 1;
          inputRef.current.setSelectionRange(newPos, newPos);
          inputRef.current.focus();
        }
      }, 0);
    }

    setShowFileDropdown(false);
    setFileSearchText('');
  };

  const handleFileDropdownKeyDown = (e) => {
    if (!showFileDropdown) return;

    const filteredFiles = getFilteredFiles();

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedFileIndex(prev => Math.min(prev + 1, filteredFiles.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedFileIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && filteredFiles.length > 0) {
      e.preventDefault();
      selectFile(filteredFiles[selectedFileIndex]);
    } else if (e.key === 'Escape') {
      setShowFileDropdown(false);
    }
  };

  // Función para obtener contenido de archivos referenciados
  const fetchReferencedFileContent = async (fileId) => {
    try {
      const response = await fetch(
        `${FILES_API_URL}/api/files/${fileId}/content?username=${encodeURIComponent(username)}`
      );
      const data = await response.json();
      if (data.success && data.file) {
        return data.file;
      }
      return null;
    } catch (error) {
      console.error('[CHATBOT] Error obteniendo contenido del archivo:', error);
      return null;
    }
  };

  const sendMessage = async () => {
    if (!username) {
      console.error('[CHAT] ❌ No se puede enviar mensaje sin username');
      alert('Error: No se pudo identificar al usuario. Por favor recarga la página.');
      return;
    }

    if ((!inputValue.trim() && !uploadedFile) || isLoading) return;

    console.log('\n[CHAT] 🚀 Enviando mensaje... | Usuario:', username);

    const currentFile = uploadedFile;
    const hasFileAttached = !!uploadedFile;
    const hasReferencedFiles = referencedFiles.length > 0;

    // Construir texto para mostrar con archivos referenciados
    let displayText = inputValue || '📎 Archivo adjunto';
    const referencedFileNames = referencedFiles.map(f => f.original_name);

    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: displayText,
      timestamp: new Date(),
      hasFile: hasFileAttached || hasReferencedFiles,
      fileName: currentFile?.filename || (hasReferencedFiles ? referencedFileNames.join(', ') : null),
      fileType: currentFile?.type || (hasReferencedFiles ? 'reference' : null),
      fileIcon: currentFile?.type === 'excel' ? '📊' : currentFile?.type === 'csv' ? '📊' : currentFile?.type === 'pdf' ? '📄' : hasReferencedFiles ? '📁' : '🖼️'
    };

    setMessages(prev => [...prev, userMessage]);
    const queryText = inputValue;
    setInputValue('');
    setIsLoading(true);
    setStreamingMessage('');
    setThinkingMessage('');
    setShowThinking(false);
    setShowFileDropdown(false);

    // Limpiar archivo después de agregar al mensaje
    setUploadedFile(null);
    setFileClearTrigger(Date.now()); // Trigger para limpiar el FileUploader
    const currentReferencedFiles = [...referencedFiles];
    setReferencedFiles([]);

    abortControllerRef.current = new AbortController();

    try {
      const payload = {
        username: username,
        query: queryText || 'Analiza el archivo adjunto'
      };

      // Obtener contenido de archivos referenciados con @
      if (currentReferencedFiles.length > 0) {
        console.log('[CHAT] 📁 Procesando archivos referenciados:', currentReferencedFiles.map(f => f.original_name));

        for (const refFile of currentReferencedFiles) {
          const fileContent = await fetchReferencedFileContent(refFile.id);
          if (fileContent) {
            // Agregar cada archivo según su tipo
            if (refFile.file_type === 'csv' || refFile.file_type === 'excel') {
              // CSV y Excel se envían como excel_data para que lo procese
              payload.excel_data = fileContent.base64;
              payload.excel_filename = refFile.original_name;
              console.log('[CHAT] 📊 Archivo de datos referenciado preparado:', refFile.original_name, '(tipo:', refFile.file_type + ')');
            } else if (refFile.file_type === 'pdf') {
              payload.pdf_data = fileContent.base64;
              payload.pdf_filename = refFile.original_name;
              console.log('[CHAT] 📄 PDF referenciado preparado:', refFile.original_name);
            }
          }
        }
      }

      // Agregar archivo subido directamente según su tipo
      if (currentFile) {
        console.log(`[CHAT] 📁 Adjuntando ${currentFile.type}:`, currentFile.filename);

        switch (currentFile.type) {
          case 'image':
            payload.image_data = currentFile.base64;
            payload.image_mime_type = currentFile.mimeType;
            payload.image_filename = currentFile.filename;
            console.log('[CHAT] 🖼️ Imagen preparada');
            break;

          case 'excel':
          case 'csv':
            // Excel y CSV se procesan igual en el backend
            payload.excel_data = currentFile.base64;
            payload.excel_filename = currentFile.filename;
            console.log(`[CHAT] 📊 ${currentFile.type.toUpperCase()} preparado:`, {
              filename: currentFile.filename,
              hasBase64: !!currentFile.base64,
              base64Length: currentFile.base64?.length || 0
            });
            break;

          case 'pdf':
            payload.pdf_data = currentFile.base64;
            payload.pdf_filename = currentFile.filename;
            console.log('[CHAT] 📄 PDF preparado');
            break;
        }
      }

      // Log del payload (sin base64 completo)
      const payloadDebug = {
        ...payload,
        excel_data: payload.excel_data ? `[BASE64: ${payload.excel_data.length} chars]` : undefined,
        pdf_data: payload.pdf_data ? `[BASE64: ${payload.pdf_data.length} chars]` : undefined,
        image_data: payload.image_data ? `[BASE64: ${payload.image_data.length} chars]` : undefined
      };
      console.log('[CHAT] 📤 Payload:', JSON.stringify(payloadDebug, null, 2));

      console.log('[CHAT] 📡 Conectando al servidor... | Usuario:', username);
      const response = await fetch(`${BACKEND_URL}/query-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      console.log('[CHAT] ✅ Conexión establecida');
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          console.log('[CHAT] ✅ Stream finalizado');
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'start') {
                console.log('[CHAT] 🌊 Stream iniciado');
                setShowThinking(true);
              } 
              else if (data.type === 'thinking') {
                setThinkingMessage(data.accumulated);
              }
              else if (data.type === 'tool_use') {
                console.log('[CHAT] 🔧 Usando tool:', data.tool);
              }
              else if (data.type === 'final_response_start') {
                console.log('[CHAT] 🎯 Respuesta final iniciando');
                setShowThinking(false);
                setThinkingMessage('');
                accumulatedText = '';
              }
              else if (data.type === 'chunk') {
                accumulatedText = data.accumulated || accumulatedText + data.content;
                setStreamingMessage(accumulatedText);
              } 
              else if (data.type === 'done') {
                const finalText = data.content || accumulatedText;
                console.log('[CHAT] ✅ Completado');
                
                setMessages(prev => [...prev, {
                  id: Date.now(),
                  type: 'bot',
                  text: finalText,
                  timestamp: new Date()
                }]);
                
                setStreamingMessage('');
                setThinkingMessage('');
                setShowThinking(false);
              } 
              else if (data.type === 'error') {
                console.error('[CHAT] ❌ Error:', data.error);
                throw new Error(data.error);
              }
            } catch (parseError) {
              if (parseError.message !== 'Unexpected end of JSON input') {
                console.error('[CHAT] ⚠️ Error parsing:', parseError);
              }
            }
          }
        }
      }

      setIsLoading(false);

    } catch (error) {
      console.error('[CHAT] ❌ Error:', error);
      setIsLoading(false);
      setStreamingMessage('');
      setThinkingMessage('');
      setShowThinking(false);
      
      if (error.name === 'AbortError') {
        console.log('[CHAT] ⚠️ Cancelado');
        return;
      }

      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'bot',
        text: `❌ **Error de conexión:** ${error.message}\n\nAsegúrate de que la API esté corriendo y que tu usuario tenga acceso.`,
        timestamp: new Date(),
        isError: true
      }]);
    }
  };

  const handleKeyPress = (e) => {
    // Si el dropdown está abierto, manejar navegación
    if (showFileDropdown) {
      handleFileDropdownKeyDown(e);
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTimestamp = (date) => {
    return date.toLocaleTimeString('es-MX', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const clearChat = () => {
    const langMessages = WELCOME_MESSAGES[language] || WELCOME_MESSAGES.es;
    setMessages([
      {
        id: 1,
        type: 'bot',
        text: langMessages.cleared,
        timestamp: new Date()
      }
    ]);
    setStreamingMessage('');
    setThinkingMessage('');
    setShowThinking(false);
    setUploadedFile(null);
  };

  const MarkdownTableWithDownload = ({ children }) => {
    const tableRef = useRef(null);

    const handleDownloadTable = () => {
      try {
        const table = tableRef.current;
        const rows = table.querySelectorAll('tr');
        const data = [];

        rows.forEach((row, index) => {
          const cells = row.querySelectorAll('th, td');
          const rowData = {};

          cells.forEach((cell, cellIndex) => {
            if (index === 0) {
              rowData[cell.textContent.trim()] = cell.textContent.trim();
            } else {
              const headers = Array.from(table.querySelectorAll('tr:first-child th, tr:first-child td'));
              const headerText = headers[cellIndex]?.textContent.trim() || `Column_${cellIndex + 1}`;
              rowData[headerText] = cell.textContent.trim();
            }
          });

          if (index > 0) {
            data.push(rowData);
          }
        });

        const timestamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\..+/, '');
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos');
        XLSX.writeFile(workbook, `tabla_databunker_${timestamp}.xlsx`);
      } catch (error) {
        console.error('Error al descargar tabla:', error);
        alert('Error al generar el archivo Excel');
      }
    };

    return (
      <div className="markdown-table-wrapper">
        <div className="table-header">
          <button
            className="download-table-btn"
            onClick={handleDownloadTable}
            title="Descargar tabla como Excel"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Descargar Excel
          </button>
        </div>
        <div className="markdown-table-container">
          <table className="markdown-table" ref={tableRef}>
            {children}
          </table>
        </div>
      </div>
    );
  };

  if (!username) {
    return (
      <div style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        background: '#fee',
        border: '2px solid #f00',
        padding: '15px',
        borderRadius: '8px',
        zIndex: 9999
      }}>
        <strong>⚠️ Error:</strong> ChatBot sin usuario autenticado
      </div>
    );
  }

  return (
    <>
      <button 
        className={`chat-fab ${isOpen ? 'chat-fab-open' : ''}`}
        onClick={toggleChat}
        aria-label="Toggle AI Assistant"
        title="AI Pricing Analytics Engine"
      >
        {isOpen ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="currentColor">
            {/* Estrella grande central */}
            <path d="M12 5L14 12L12 19L10 12L12 5Z" />
            <path d="M5 12L12 14L19 12L12 10L5 12Z" />
            {/* Estrella arriba derecha */}
            <path d="M20 0L21.5 4.5L20 9L18.5 4.5L20 0Z" />
            <path d="M15.5 4.5L20 6L24 4.5L20 3L15.5 4.5Z" />
            {/* Estrella abajo izquierda */}
            <path d="M4 15L5.5 19.5L4 24L2.5 19.5L4 15Z" />
            <path d="M0 19.5L4 21L8 19.5L4 18L0 19.5Z" />
          </svg>
        )}
      </button>

      <div className={`chat-panel ${isOpen ? 'chat-panel-open' : ''} ${isExpanded ? 'chat-panel-expanded' : ''}`}>
        <div className="chat-header">
          <div className="chat-header-content">
            <div className="chat-header-avatar">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 5L14 12L12 19L10 12L12 5Z" />
                <path d="M5 12L12 14L19 12L12 10L5 12Z" />
                <path d="M20 0L21.5 4.5L20 9L18.5 4.5L20 0Z" />
                <path d="M15.5 4.5L20 6L24 4.5L20 3L15.5 4.5Z" />
                <path d="M4 15L5.5 19.5L4 24L2.5 19.5L4 15Z" />
                <path d="M0 19.5L4 21L8 19.5L4 18L0 19.5Z" />
              </svg>
            </div>
            <div className="chat-header-info">
              <h3>AI Pricing Analytics</h3>
              <span className="chat-status">
                <span className="chat-status-dot"></span>
                Online
              </span>
            </div>
          </div>
          <div className="chat-header-actions">
            <button
              className="chat-header-btn"
              onClick={toggleExpand}
              title={isExpanded ? "Restaurar tamaño" : "Expandir"}
            >
              {isExpanded ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                </svg>
              )}
            </button>
            <button
              className="chat-header-btn"
              onClick={clearChat}
              title="Limpiar chat"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
              </svg>
            </button>
            <button
              className="chat-header-btn"
              onClick={toggleChat}
              title="Minimizar"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        <div className="chat-messages">
          {messages.map((message) => (
            <div 
              key={message.id} 
              className={`chat-message ${message.type === 'user' ? 'chat-message-user' : 'chat-message-bot'} ${message.isError ? 'chat-message-error' : ''}`}
            >
              {message.type === 'bot' && (
                <div className="chat-message-avatar">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 5L14 12L12 19L10 12L12 5Z" />
                    <path d="M5 12L12 14L19 12L12 10L5 12Z" />
                    <path d="M20 0L21.5 4.5L20 9L18.5 4.5L20 0Z" />
                    <path d="M15.5 4.5L20 6L24 4.5L20 3L15.5 4.5Z" />
                    <path d="M4 15L5.5 19.5L4 24L2.5 19.5L4 15Z" />
                    <path d="M0 19.5L4 21L8 19.5L4 18L0 19.5Z" />
                  </svg>
                </div>
              )}
              <div className="chat-message-content">
                {message.hasFile && message.fileName && (
                  <div className="message-file-badge">
                    <span className="file-badge-icon">{message.fileIcon}</span>
                    <span className="file-badge-name">{message.fileName}</span>
                    <span className="file-badge-type">{message.fileType}</span>
                  </div>
                )}
                <div className="chat-message-text">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      code({ node, inline, className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || '');
                        return !inline && match ? (
                          <SyntaxHighlighter
                            style={tomorrow}
                            language={match[1]}
                            PreTag="div"
                            {...props}
                          >
                            {String(children).replace(/\n$/, '')}
                          </SyntaxHighlighter>
                        ) : (
                          <code className={className} {...props}>
                            {children}
                          </code>
                        );
                      },
                      table({ children }) {
                        return <MarkdownTableWithDownload>{children}</MarkdownTableWithDownload>;
                      },
                      thead({ children }) {
                        return <thead className="markdown-thead">{children}</thead>;
                      },
                      tbody({ children }) {
                        return <tbody className="markdown-tbody">{children}</tbody>;
                      },
                      tr({ children }) {
                        return <tr className="markdown-tr">{children}</tr>;
                      },
                      th({ children }) {
                        return <th className="markdown-th">{children}</th>;
                      },
                      td({ children }) {
                        return <td className="markdown-td">{children}</td>;
                      }
                    }}
                  >
                    {message.text}
                  </ReactMarkdown>
                </div>
                <span className="chat-message-time">
                  {formatTimestamp(message.timestamp)}
                </span>
              </div>
            </div>
          ))}
          
          {showThinking && thinkingMessage && (
            <div className="chat-message chat-message-bot chat-message-thinking">
              <div className="chat-message-avatar">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="thinking-icon">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
              </div>
              <div className="chat-message-content">
                <div className="thinking-label">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                    <circle cx="12" cy="12" r="1" />
                    <circle cx="19" cy="12" r="1" />
                    <circle cx="5" cy="12" r="1" />
                  </svg>
                  Analizando...
                </div>
                <div className="chat-message-text thinking-text">
                  {thinkingMessage}
                </div>
              </div>
            </div>
          )}
          
          {streamingMessage && (
            <div className="chat-message chat-message-bot chat-message-streaming">
              <div className="chat-message-avatar">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 5L14 12L12 19L10 12L12 5Z" />
                  <path d="M5 12L12 14L19 12L12 10L5 12Z" />
                  <path d="M20 0L21.5 4.5L20 9L18.5 4.5L20 0Z" />
                  <path d="M15.5 4.5L20 6L24 4.5L20 3L15.5 4.5Z" />
                  <path d="M4 15L5.5 19.5L4 24L2.5 19.5L4 15Z" />
                  <path d="M0 19.5L4 21L8 19.5L4 18L0 19.5Z" />
                </svg>
              </div>
              <div className="chat-message-content">
                <div className="chat-message-text">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {streamingMessage}
                  </ReactMarkdown>
                </div>
                <span className="chat-typing-cursor">▊</span>
              </div>
            </div>
          )}
          
          {isLoading && !streamingMessage && !thinkingMessage && (
            <div className="chat-message chat-message-bot">
              <div className="chat-message-avatar">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 5L14 12L12 19L10 12L12 5Z" />
                  <path d="M5 12L12 14L19 12L12 10L5 12Z" />
                  <path d="M20 0L21.5 4.5L20 9L18.5 4.5L20 0Z" />
                  <path d="M15.5 4.5L20 6L24 4.5L20 3L15.5 4.5Z" />
                  <path d="M4 15L5.5 19.5L4 24L2.5 19.5L4 15Z" />
                  <path d="M0 19.5L4 21L8 19.5L4 18L0 19.5Z" />
                </svg>
              </div>
              <div className="chat-message-content">
                <div className="chat-typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-input-container">
          {/* Indicador de archivos referenciados - arriba como los adjuntados */}
          {referencedFiles.length > 0 && (
            <div className="referenced-files-preview">
              {referencedFiles.map((file) => (
                <div key={file.id} className="referenced-file-card">
                  <span className="referenced-file-icon">
                    {file.file_type === 'pdf' ? '📄' : '📊'}
                  </span>
                  <div className="referenced-file-info">
                    <span className="referenced-file-name">{file.original_name}</span>
                    <span className="referenced-file-meta">
                      {file.file_size_formatted} • {file.file_type.toUpperCase()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="chat-input-wrapper">
            {/* FileUploader - clip integrado en el input */}
            <FileUploader
              onFileUpload={handleFileUpload}
              disabled={isLoading}
              clearTrigger={fileClearTrigger}
            />

            {/* Dropdown de archivos con @ */}
            {showFileDropdown && getFilteredFiles().length > 0 && (
              <div className="file-dropdown" ref={dropdownRef}>
                <div className="file-dropdown-header">
                  <span className="file-dropdown-icon">📁</span>
                  <span>Archivos disponibles</span>
                </div>
                <div className="file-dropdown-list">
                  {getFilteredFiles().map((file, index) => (
                    <div
                      key={file.id}
                      className={`file-dropdown-item ${index === selectedFileIndex ? 'selected' : ''}`}
                      onClick={() => selectFile(file)}
                      onMouseEnter={() => setSelectedFileIndex(index)}
                    >
                      <span className="file-dropdown-item-icon">
                        {file.file_type === 'pdf' ? '📄' : '📊'}
                      </span>
                      <div className="file-dropdown-item-info">
                        <span className="file-dropdown-item-name">{file.original_name}</span>
                        <span className="file-dropdown-item-meta">
                          {file.file_size_formatted} • {file.file_type.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="file-dropdown-hint">
                  ↑↓ para navegar • Enter para seleccionar • Esc para cerrar
                </div>
              </div>
            )}

            <textarea
              ref={inputRef}
              className="chat-input"
              placeholder={
                uploadedFile
                  ? (PLACEHOLDERS[language] || PLACEHOLDERS.es).withFile
                  : hasFileAccess
                    ? (PLACEHOLDERS[language] || PLACEHOLDERS.es).withFileAccess
                    : (PLACEHOLDERS[language] || PLACEHOLDERS.es).default
              }
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyPress}
              rows="1"
              disabled={isLoading}
            />
            <button
              className="chat-send-btn"
              onClick={sendMessage}
              disabled={(!inputValue.trim() && !uploadedFile) || isLoading}
              aria-label="Enviar mensaje"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
          <p className="chat-input-hint">
            Aunque analizo datos rápidamente, la decisión final es tuya. <br/>
Mis sugerencias son solo orientativas, revisa cuidadosamente antes de actuar.
          </p>
        </div>
      </div>

      {isOpen && <div className="chat-overlay" onClick={toggleChat} />}
    </>
  );
}

export default ChatBot;